import { Context } from '@front10/helpers/dist/Context';
import dayjs from 'dayjs';
import React, { KeyboardEventHandler, MouseEventHandler, useContext, useRef } from 'react';
import { weekDaysLong } from '../../../../helpers/localization/localizationUtils';
import { mergeDeep } from '../../../../helpers/utils';
import Box from '../../../elements/Box';
import { formatDate } from '../../DatesRangeSelector/commons';
import { DayData } from '../types';
import {
  addMonthsNumber,
  getDayData,
  getFirstDayOfMonth,
  getModifiers,
  isDayInRange,
  lastDayOfMonth
} from './helper';
import {
  FnOnDayClick,
  FnOnDayHover,
  NavigationValidationFunctions,
  useCalendarDispatch,
  useCalendarState,
  useHandleCalendarNavigation,
  useModifiers
} from './hooks';
import { getClassNames } from './helper';

export type DayItemProps = {
  dayData: DayData;
  onDayClick: FnOnDayClick;
  onDayHover: FnOnDayHover;
  setWasOpenPopup?: (value: boolean) => void;
  componentName?: string;
  enableAvailableMessage?: boolean;
};

const DayItemMobile = (props: DayItemProps) => {
  const {
    dayData,
    onDayHover,
    onDayClick,
    setWasOpenPopup,
    componentName,
    enableAvailableMessage,
    ...restProps
  } = props;

  //hooks
  const { state, format, modifiers, numberOfMonths } = useCalendarState();
  const dispatch = useCalendarDispatch();

  // validations functions
  const validations: NavigationValidationFunctions = {
    validateDay: (date: Date) => {
      const dateModifiers = getModifiers(date, modifiers);
      return (
        !dateModifiers.disabled &&
        dateModifiers.interactive &&
        !dateModifiers.blackout &&
        !dateModifiers.hidden
      );
    },
    dataFromDay: (date: Date) => {
      return getDayData(date, state.months);
    },
    isDateOutOfView: (date: Date) => {
      //show data when are off screen
      const endDisplayRange = lastDayOfMonth(
        addMonthsNumber(state.displayDate, numberOfMonths - 1)
      );
      const startDisplayRange = getFirstDayOfMonth(state.displayDate);
      return !isDayInRange(date, { start: startDisplayRange, end: endDisplayRange });
    }
  };

  const navigationHandler = useHandleCalendarNavigation({
    isPopopverOpen: state.popoverOpen,
    validations,
    initialDate: state?.preSelected,
    format,
    component: state.selectedComponent,
    onDayClick,
    dispatch,
    preSelected: state.preSelected,
    setWasOpenPopup: setWasOpenPopup
  });

  const ref = useRef<any>(null);
  const context = useContext(Context);
  const { translate, localizationData, direction } = context;
  const daysLabels = weekDaysLong(translate, localizationData);
  // const { classes: dayClassNames, styles: modifiersStyles, modifiers: dateModifiers, date: day } =dayData;

  const { classes: dayClassNames, style: modifiersStyles, modifier: dateModifiers } = useModifiers(
    dayData.date
  );
  const day = dayData.date;

  const isPreselectedDay = dateModifiers.preSelected;

  const newPropsModifier = mergeDeep(restProps, modifiersStyles);

  //events handlers
  const handleSelectDay: MouseEventHandler<HTMLDivElement> | undefined = !dateModifiers.interactive
    ? undefined
    : (event) => {
        event.preventDefault();
        if (day) {
          onDayClick(dayData);
        }
      };

  const handleKeyDown: KeyboardEventHandler<HTMLDivElement> | undefined = !dateModifiers.interactive
    ? undefined
    : navigationHandler;

  const tabIndex = isPreselectedDay ? 0 : !dateModifiers.interactive ? undefined : -1;

  if (!day) {
    return (
      <Box
        type="td"
        {...newPropsModifier}
        flex={1}
        padding="0"
        className={`calendar-day fr-datepicker-empty ${dayClassNames}`}
      >
        &nbsp;
      </Box>
    );
  }

  const getAriaLabel = () => {
    const formatedDate = formatDate(dayjs(day).toDate(), format, context);
    const dayName = day ? daysLabels[day?.getDay()] : '';
    const prefixArialLabel = dateModifiers.interactive
      ? translate('date-picker-date-available', `Choose`)
      : translate('date-picker-date-not-available', `Not available`);

    return `${enableAvailableMessage ? prefixArialLabel : ''} ${dayName} ${formatedDate}`;
  };

  const focusClass =
    state.focusSelectedDay && state.preSelected && dayjs(day).isSame(state.preSelected, 'day')
      ? 'datepicker-focus-day'
      : '';

  let dayContainerClassNames = getClassNames(
    dayClassNames,
    componentName,
    state?.selectedRange,
    day,
    state?.preSelected,
    direction
  );

  const cellStyleProps = {
    'cmp-styles': {
      'min-width': `${newPropsModifier['cmp-styles']['min-width']}`
    }
  };

  return (
    // https://www.w3.org/TR/wai-aria-1.1/#cell
    // eslint-disable-next-line jsx-a11y/role-supports-aria-props
    <Box
      key={`calendar-day-${day?.getDate()} -${day?.getMonth()}`}
      onBlur={() => {
        dispatch({ type: 'SET_FOCUS_SELECTED_DAY', payload: false });
      }}
      onFocus={() => {
        dispatch({ type: 'SET_FOCUS_SELECTED_DAY', payload: true });
      }}
      data-testid="calendar-day-item"
      focusReferences={ref}
      className="calendar-day"
      onClick={handleSelectDay}
      onMouseEnter={(event) => {
        onDayHover(event, dayData);
      }}
      onKeyDown={handleKeyDown}
      tabIndex={tabIndex}
      aria-disabled={dateModifiers.disabled}
      ariaLabel={getAriaLabel()}
      type="td"
      role="cell"
      flex={1}
      {...cellStyleProps}
    >
      <Box className={`calendar-day-wrapper ${dayContainerClassNames}`}>
        <Box
          className={`calendar-day-inner ${dayClassNames} ${focusClass}`}
          {...newPropsModifier}
          style={{ width: '100%', ...newPropsModifier.style }}
        >
          {day?.getDate()}
        </Box>
      </Box>
    </Box>
  );
};

export default DayItemMobile;
